import { Component, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import Chart from 'chart.js/auto';
declare let $: any;

export interface SafetyVehicle {
  strUnitID: string;
  strVehicleName?: string | null;
  strName?: string | null;
  strFullName?: string | null;
  strVehicleNo?: string | null;
  dtSDateTime: string;
  dtEDateTime: string;
  SafetyIndex: number | null;
  AcceleratorScore: number | null;
  TorqueScore: number | null;
  SpeedScore: number | null;
  StopGoScore: number | null;
  MaxSpeed: number | null;
  TotalRiskEvents: number | null;
  TotalHighEvents: number | null;
  TotalVeryHighEvents: number | null;
  AcceleratorEvents: number | null;
  TorqueEvents: number | null;
}

export interface SafetyStatus {
  label: string;
  color: string;
  bg: string;
  badge: 'safe' | 'warning' | 'danger' | 'critical';
}

export const STATUS_MAP: Record<string, SafetyStatus> = {
  safe: { label: 'ایمن', color: '#2ecc71', bg: '#1e3a2e', badge: 'safe' },
  warning: { label: 'نیازمند توجه', color: '#f1c40f', bg: '#3d3a1a', badge: 'warning' },
  danger: { label: 'پرخطر', color: '#e74c3c', bg: '#3d1f1a', badge: 'danger' },
  critical: { label: 'بسیار پرخطر', color: '#b03a2e', bg: '#2e1512', badge: 'critical' },
};

@Component({
  selector: 'app-safety-vehicle-modal',
  templateUrl: './safety-vehicle-modal.component.html',
  styleUrls: ['./safety-vehicle-modal.component.css'],
})

export class SafetyVehicleModalComponent implements AfterViewInit {
  vehicle: SafetyVehicle | null = null;
  scoreCards: { label: string; value: number | null | undefined; percent: number; color: string }[] = [];

  private gaugeChart: any = null;
  private radarChart: any = null;
  private bsInstance: any = null;

  constructor(private cdr: ChangeDetectorRef) {
    Chart.defaults.font.family = "'vazir', Tahoma, sans-serif";
  }

  ngAfterViewInit(): void {
    const el = document.getElementById('safetyVehicleModal');
    if (el) {
      el.addEventListener('hidden.bs.modal', () => this.destroyCharts());
    }
  }

  open(v: SafetyVehicle): void {
    this.destroyCharts();
    this.vehicle = v;
    this.scoreCards = this.buildScoreCards(v);
    setTimeout(() => {
      const el = document.getElementById('safetyVehicleModal');
      if (!el) return;
      el.addEventListener('shown.bs.modal',() => {
          this.cdr.detectChanges();
          this.renderCharts();
        },
        { once: true }
      );
      this.showBootstrap(el);
      setTimeout(() => {
        if (!this.gaugeChart && this.vehicle) {
          this.cdr.detectChanges();
          this.renderCharts();
        }
      }, 600);
    }, 30);
  }

  close(): void {
    try {
      if (this.bsInstance) {
        this.bsInstance.hide();
      } else {
        const jq: any = typeof $ !== 'undefined' ? $('#safetyVehicleModal') : null;
        if (jq && typeof jq.modal === 'function') jq.modal('hide');
      }
    } catch {}
  }

  getStatus(safetyIndex: number | null | undefined): SafetyStatus {
    if (safetyIndex === null || safetyIndex === undefined) return STATUS_MAP['critical'];
    if (safetyIndex >= 80) return STATUS_MAP['safe'];
    if (safetyIndex >= 60) return STATUS_MAP['warning'];
    if (safetyIndex >= 40) return STATUS_MAP['danger'];
    return STATUS_MAP['critical'];
  }

  vehicleName(v: SafetyVehicle | null): string {
    if (!v) return '—';
    return v.strFullName || v.strUnitID || '—';
  }

  toFa(n: number | null | undefined): string {
    if (n === null || n === undefined) return '—';
    return n.toLocaleString('fa-IR');
  }

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '—';
    try {
      const d = new Date((dateStr as string).replace(' ', 'T'));
      if (isNaN(d.getTime())) return dateStr as string;
      return (
        d.toLocaleDateString('fa-IR') +
        ' ' +
        d.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })
      );
    } catch {
      return dateStr as string;
    }
  }

  scoreColor(val: number | null | undefined): string {
    const p = val ?? 0;
    if (p >= 80) return '#2ecc71';
    if (p >= 60) return '#f1c40f';
    if (p >= 40) return '#e74c3c';
    return '#b03a2e';
  }

  private buildScoreCards(v: SafetyVehicle): { label: string; value: number | null | undefined; percent: number; color: string }[] {
    const mk = (label: string, value: number | null | undefined) => ({
      label,
      value,
      percent: Math.min(100, Math.max(0, value ?? 0)),
      color: this.scoreColor(value),
    });
    return [
      mk('شتاب‌گیری', v.AcceleratorScore),
      mk('گشتاور', v.TorqueScore),
      mk('سرعت', v.SpeedScore),
      mk('توقف و حرکت', v.StopGoScore),
    ];
  }

  private showBootstrap(el: HTMLElement): void {
    const bs = (window as any).bootstrap;
    if (bs && bs.Modal) {
      this.bsInstance = bs.Modal.getOrCreateInstance(el);
      this.bsInstance.show();
      return;
    }
    try {
      const jq: any = typeof $ !== 'undefined' ? $('#safetyVehicleModal') : null;
      if (jq && typeof jq.modal === 'function') jq.modal('show');
    } catch {}
  }

  private renderCharts(): void {
    if (!this.vehicle) return;
    this.renderGauge();
    this.renderRadar();
  }

  private renderGauge(): void {
    const canvas = document.getElementById('safetyGaugeCanvas') as HTMLCanvasElement | null;
    if (!canvas || !this.vehicle) return;
    if (this.gaugeChart) {
      this.gaugeChart.destroy();
      this.gaugeChart = null;
    }
    const st = this.getStatus(this.vehicle.SafetyIndex);
    const value = Math.min(100, Math.max(0, this.vehicle.SafetyIndex ?? 0));
    this.gaugeChart = new Chart(canvas, {
      type: 'doughnut',
      data: {
        datasets: [
          {
            data: [value, 100 - value],
            backgroundColor: [st.color, '#1f2f3c'],
            borderWidth: 0,
            borderRadius: 5,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '78%',
        rotation: -90,
        circumference: 360,
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
      },
    });
  }

  private renderRadar(): void {
    const canvas = document.getElementById('safetyModalRadar') as HTMLCanvasElement | null;
    if (!canvas || !this.vehicle) return;
    if (this.radarChart) {
      this.radarChart.destroy();
      this.radarChart = null;
    }
    const v = this.vehicle;
    this.radarChart = new Chart(canvas, {
      type: 'radar',
      data: {
        labels: ['شتاب‌گیری', 'گشتاور', 'سرعت', 'توقف و حرکت', 'شاخص نهایی'],
        datasets: [
          {
            label: this.vehicleName(v),
            data: [
              v.AcceleratorScore ?? 0,
              v.TorqueScore ?? 0,
              v.SpeedScore ?? 0,
              v.StopGoScore ?? 0,
              v.SafetyIndex ?? 0,
            ],
            backgroundColor: 'rgba(74, 158, 255, 0.15)',
            borderColor: '#4a9eff',
            borderWidth: 2,
            pointBackgroundColor: '#4a9eff',
            pointBorderColor: '#0b0f14',
            pointRadius: 3,
            pointHoverRadius: 5,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          r: {
            beginAtZero: true,
            max: 100,
            ticks: {
              stepSize: 25,
              color: '#6f8397',
              backdropColor: 'transparent',
              font: { size: 8, family: "'vazir', Tahoma, sans-serif" },
            },
            grid: { color: '#1f2f3c' },
            angleLines: { color: '#1f2f3c' },
            pointLabels: { color: '#b0c2d4', font: { size: 9, family: "'vazir', Tahoma, sans-serif" } },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#111a22',
            borderColor: '#2a3a48',
            borderWidth: 1,
            titleColor: '#e0e9f2',
            bodyColor: '#b0c2d4',
            cornerRadius: 12,
            padding: 12,
            titleFont: { family: "'vazir', Tahoma, sans-serif" },
            bodyFont: { family: "'vazir', Tahoma, sans-serif" },
          },
        },
      },
    });
  }

  private destroyCharts(): void {
    if (this.gaugeChart) {
      this.gaugeChart.destroy();
      this.gaugeChart = null;
    }
    if (this.radarChart) {
      this.radarChart.destroy();
      this.radarChart = null;
    }
  }
}
